import font300 from '@fontsource/plus-jakarta-sans/files/plus-jakarta-sans-all-300-normal.woff';
import font400 from '@fontsource/plus-jakarta-sans/files/plus-jakarta-sans-all-400-normal.woff';
import font500 from '@fontsource/plus-jakarta-sans/files/plus-jakarta-sans-all-500-normal.woff';
import font700 from '@fontsource/plus-jakarta-sans/files/plus-jakarta-sans-all-700-normal.woff';
import { Document, Ellipse, Font, G, Page, Path, StyleSheet, Svg, Text, View } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';
import { InvoiceFormType, InvoiceManagementInvoiceFormType } from '../../forms/invoice/types';

function pixelsToPoints(px: number): number {
  const pixelsPerInch = 96; // Assuming a standard screen resolution of 96 DPI
  const pointsPerInch = 72;
  // Calculate the points from pixels
  return px * (pointsPerInch / pixelsPerInch);
}

Font.register({
  family: 'Plus Jakarta Sans',
  fonts: [
    {
      fontWeight: 300,
      src: font300,
    },
    {
      fontWeight: 400,
      src: font400,
    },
    {
      fontWeight: 500,
      src: font500,
    },
    {
      fontWeight: 400,
      src: font700,
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Plus Jakarta Sans',
    flexDirection: 'column',
    gap: pixelsToPoints(36),
    paddingHorizontal: 25,
    paddingVertical: 35,
  },
  'content-h-gap': {
    gap: pixelsToPoints(28),
  },
  'offset--small': {
    marginTop: pixelsToPoints(16),
  },
  offset: {
    marginTop: pixelsToPoints(8),
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: pixelsToPoints(16),
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
  },
  divider: {
    borderTop: 1,
    borderTopStyle: 'dashed',
    borderTopColor: 'rgb(217, 217, 217)',
    borderBottom: 0,
    borderLeft: 0,
    borderRight: 0,
    height: pixelsToPoints(1),
  },
  section: {
    flex: 1,
  },
  section__header: {
    color: 'rgba(0, 0, 0, 0.87)',
    fontWeight: 500,
    fontSize: pixelsToPoints(22),
  },
  dataEntry__container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  dataEntry__label: {
    fontSize: pixelsToPoints(12),
    fontWeight: 400,
    color: 'rgba(0, 0, 0, 0.6)',
    whiteSpace: 'nowrap',
  },
  dataEntry__value: {
    color: 'rgba(0, 0, 0, 0.87)',
    fontWeight: 400,
    fontSize: pixelsToPoints(16),
  },
  footer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 25,
    paddingVertical: 10,
    alignItems: 'center',
    borderTop: 1,
    borderTopStyle: 'dashed',
    borderTopColor: 'rgb(217, 217, 217)',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    paddingBottom: 10,
    alignItems: 'center',
    borderBottom: 1,
    borderBottomStyle: 'dashed',
    borderBottomColor: 'rgb(217, 217, 217)',
  },
  footer__text: {
    textAlign: 'center',
    fontSize: pixelsToPoints(10),
    fontWeight: 300,
    color: 'rgba(0, 0, 0, 0.5)',
  },
});

export const InvoiceManagementInvoicePDFDocument = ({
  data,
}: {
  data: Omit<InvoiceManagementInvoiceFormType, 'amount' | 'amountWithoutVat' | 'amountVat'> & {
    amount: string;
    amountWithoutVat: string;
    amountVat: string;
  };
}) => {
  const { t } = useTranslation();

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View fixed style={styles.header}>
          <AppLogo />
        </View>
        <View style={[styles.row, styles['content-h-gap']]}>
          <View style={styles.section}>
            <Text style={styles.section__header}>{t('invoice')}</Text>
          </View>
          <View style={styles.section}>
            <DataEntry label={t('invoice.number')} value={data.number} />
          </View>
        </View>
        <View style={[styles.row, styles['content-h-gap']]}>
          <View style={styles.section}>
            <Text style={styles.section__header}>{t('vendor')}</Text>
            <View style={[styles.row, styles.offset]}>
              <View style={styles.section}>
                <DataEntry label={t('vatId')} value={data.vendor?.vatId} />
              </View>
              <View style={styles.section}>
                <DataEntry label={t('taxId')} value={data.vendor?.taxId} />
              </View>
            </View>
            <View style={[styles.row, styles['offset--small']]}>
              <View style={styles.section}>
                <DataEntry label={t('businessName')} value={data.vendor?.name} />
              </View>
            </View>
            <View style={[styles.row, styles['offset--small']]}>
              <View style={styles.section}>
                <DataEntry label={t('streetAddress')} value={data.vendor?.address} />
              </View>
            </View>
          </View>
          <View style={styles.section}>
            <Text style={styles.section__header}>{t('customer')}</Text>
            <View style={[styles.row, styles.offset]}>
              <View style={styles.section}>
                <DataEntry label={t('vatId')} value={data.customer?.vatId} />
              </View>
              <View style={styles.section}>
                <DataEntry label={t('taxId')} value={data.customer?.taxId} />
              </View>
            </View>
            <View style={[styles.row, styles['offset--small']]}>
              <View style={styles.section}>
                <DataEntry label={t('businessName')} value={data.customer?.name} />
              </View>
            </View>
            <View style={[styles.row, styles['offset--small']]}>
              <View style={styles.section}>
                <DataEntry label={t('streetAddress')} value={data.customer?.address} />
              </View>
            </View>
            {data.customer?.email ? (
              <View style={[styles.row, styles['offset--small']]}>
                <View style={styles.section}>
                  <DataEntry label={t('email')} value={data.customer.email} />
                </View>
              </View>
            ) : null}
            {data.customer?.phoneNumber ? (
              <View style={[styles.row, styles['offset--small']]}>
                <View style={styles.section}>
                  <DataEntry label={t('phone')} value={data.customer.phoneNumber} />
                </View>
              </View>
            ) : null}
          </View>
        </View>
        <View style={styles['content-h-gap']}>
          <View style={styles.section}>
            <Text style={styles.section__header}>{t('paymentInfo')}</Text>
          </View>
          <View style={[styles.row, styles.offset]}>
            <View style={styles.section}>
              <View style={styles.row}>
                <View style={styles.section}>
                  <DataEntry label={t('issueDate')} value={data.issueDate} />
                </View>
                <View style={styles.section}>
                  <DataEntry label={t('dueDate')} value={data.dueDate} />
                </View>
              </View>
              {data.taxPayer && (
                <View style={[styles.row, styles['offset--small']]}>
                  <View style={styles.section}>
                    <DataEntry label={t('taxPoint')} value={data.taxPoint} />
                  </View>
                </View>
              )}
              <View style={[styles.row, styles['offset--small']]}>
                <View style={styles.section}>
                  <DataEntry label={t('variableSymbol')} value={data.variableSymbol} />
                </View>
              </View>
            </View>
            <View style={styles.section}>
              <View style={styles.row}>
                <View style={styles.section}>
                  <DataEntry label={t('bankAccount')} value={data.bankAccount} />
                </View>
                <DataEntry label={' '} value={'/'} />
                <View style={styles.section}>
                  <DataEntry label={t('bankNumber')} value={data.bankNumber} />
                </View>
              </View>
              <View style={[styles.row, styles['offset--small']]}>
                <View style={styles.section}>
                  <DataEntry label={t('iban')} value={data.iban} />
                </View>
                <View style={styles.section}>
                  <DataEntry label={t('swift')} value={data.swift} />
                </View>
              </View>
            </View>
          </View>
        </View>

        <View style={styles['content-h-gap']}>
          {data.items?.length ? (
            <View style={styles.section}>
              <Text style={styles.section__header}>Položky faktury</Text>
            </View>
          ) : null}
          <View>
            {data.items?.map((item, i) => (
              <View key={i} style={[styles.row, i > 0 ? styles.offset : {}]}>
                <View style={{ width: '30%' }}>
                  <DataEntry label={i > 0 ? '' : 'Označení dodávky'} value={item.name} />
                </View>
                <View style={[styles.section, styles.row]}>
                  <View style={styles.section}>
                    <DataEntry label={i > 0 ? '' : 'Množství'} value={item.quantity} />
                  </View>
                  <View style={styles.section}>
                    <DataEntry label={i > 0 ? '' : 'm.j.'} value={item.unit} />
                  </View>
                  <View style={[styles.section, { minWidth: 15 }]}>
                    <DataEntry label={i > 0 ? '' : 'Cena za m.j.'} value={item.pricePerUnit} />
                  </View>
                  {data.taxPayer && item.priceWithoutVat && (
                    <View style={[styles.section, { minWidth: 35 }]}>
                      <DataEntry label={i > 0 ? '' : 'Celkem bez DPH'} value={item.priceWithoutVat} />
                    </View>
                  )}
                  {data.taxPayer && item.vatRate && (
                    <View style={[styles.section, { minWidth: 15 }]}>
                      <DataEntry label={i > 0 ? '' : 'DPH (%)'} value={item.vatRate} />
                    </View>
                  )}
                  <View style={[styles.section, { minWidth: 30 }]}>
                    <DataEntry label={i > 0 ? '' : 'Celkem'} value={item.priceSum} />
                  </View>
                </View>
              </View>
            ))}
          </View>
          <View style={[styles.divider]} />
          {data.taxPayer && (
            <>
              <View style={[styles.row]}>
                <View style={styles.section}>
                  <DataEntry label={t('invoice.amountWithoutVat')} value={data.amountWithoutVat} />
                </View>
              </View>
              <View style={[styles.row]}>
                <View style={styles.section}>
                  <DataEntry label={t('invoice.amountVat')} value={data.amountVat} />
                </View>
              </View>
            </>
          )}
          <View style={[styles.row]}>
            <View style={styles.section}>
              <DataEntry label={t('invoice.amount')} value={data.amount} />
            </View>
            <View style={styles.section}>
              <DataEntry label={t('currency')} value={data.currency} />
            </View>
          </View>
        </View>

        <View fixed style={styles.footer}>
          <AppLogo />
          <Text style={styles['footer__text']}>www.upominej.cz</Text>
        </View>
      </Page>
    </Document>
  );
};

const DataEntry = ({ label, value }: { label: string; value?: string | null | number | Date }) => {
  const getValue = (): string => {
    if (!value) {
      return '-';
    }
    return value.toString();
  };
  return (
    <View style={styles.dataEntry__container} debug={false}>
      <Text style={styles.dataEntry__label}>{label}</Text>
      <Text style={styles.dataEntry__value}>{getValue()}</Text>
    </View>
  );
};

const AppLogo = () => (
  <Svg viewBox={'0 0 480 112'} width={60} height={20}>
    <Path
      d="M27.8213 69.0151C19.7299 69.0151 15.7396 61.7913 15.7396 53.345V27.7839H0V55.1232C0 71.2378 9.53241 84.2406 27.8213 84.2406C46.2211 84.2406 55.7535 71.2378 55.7535 55.1232V27.7839H40.0139V53.2339C40.0139 61.7913 35.9128 69.0151 27.8213 69.0151Z"
      fill="#282E44"
    />
    <Path
      d="M80.7345 108.135V79.2395C84.8356 82.3513 90.0452 84.3518 95.5873 84.3518C112.657 84.3518 124.406 72.3491 124.406 55.1232C124.406 39.3419 111.659 26.228 94.7006 26.228C77.6309 26.228 64.9949 38.564 64.9949 55.1232V108.135H80.7345ZM94.7006 69.4596C86.6091 69.4596 80.7345 63.0138 80.7345 55.2343C80.7345 47.4548 86.6091 41.1201 94.7006 41.1201C102.792 41.1201 108.667 47.4548 108.667 55.2343C108.667 63.0138 102.792 69.4596 94.7006 69.4596Z"
      fill="#282E44"
    />
    <Path
      d="M158.53 84.2406C175.378 84.2406 188.346 71.1266 188.346 55.1232C188.346 39.3419 175.489 26.1168 158.53 26.1168C141.793 26.1168 128.824 39.2308 128.824 55.1232C128.824 71.1266 141.682 84.2406 158.53 84.2406ZM158.53 69.4596C150.438 69.4596 144.564 63.0138 144.564 55.2343C144.564 47.4548 150.438 41.009 158.53 41.009C166.621 41.009 172.607 47.4548 172.607 55.2343C172.607 63.0138 166.621 69.4596 158.53 69.4596Z"
      fill="#282E44"
    />
    <Path
      d="M264.655 25.7834C254.79 25.7834 247.031 30.4511 243.595 38.0083C240.381 30.4511 232.733 25.7834 222.203 25.7834C204.468 25.7834 195.49 38.6751 195.49 54.0118V82.7959H211.229V56.5679C211.229 48.2328 214.665 41.1201 223.422 41.1201C231.957 41.1201 235.614 48.1216 235.614 56.5679V82.7959H251.354V56.4568C251.354 48.1216 255.012 41.1201 263.547 41.1201C272.192 41.1201 275.85 48.2328 275.85 56.6791V82.7959H291.59V54.0118C291.59 38.6751 282.501 25.7834 264.655 25.7834Z"
      fill="#282E44"
    />
    <Path
      d="M318.561 19.1153L332.637 0.666817H316.455L302.932 19.1153H318.561ZM303.708 27.7839V82.7959H319.447V27.7839H303.708Z"
      fill="#282E44"
    />
    <Path
      d="M425.911 84.2406C437.549 84.2406 446.638 78.5727 451.405 68.6817L439.323 62.5692C436.773 67.6815 431.786 70.571 425.8 70.571C417.93 70.571 412.277 65.681 411.169 58.3461H451.959V53.6784C451.959 38.6751 441.872 25.7834 424.47 25.7834C407.511 25.7834 395.984 38.8974 395.984 54.7898C395.984 72.3492 408.841 84.2406 425.911 84.2406ZM411.834 49.5664C412.942 44.0096 417.487 39.5642 424.359 39.5642C431.01 39.5642 435.665 43.8985 436.33 49.5664H411.834Z"
      fill="#282E44"
    />
    <Path
      d="M470.357 19.5598C475.677 19.5598 480 15.2256 480 9.77992C480 4.44542 475.677 0 470.357 0C465.036 0 460.603 4.44542 460.603 9.77992C460.603 15.2256 465.036 19.5598 470.357 19.5598ZM462.376 27.7839V85.1297C462.376 90.5754 458.94 94.354 451.846 94.354L452.733 109.246C469.248 109.246 478.116 99.5773 478.116 85.6854V27.7839H462.376Z"
      fill="#282E44"
    />
    <Path
      d="M360.066 40.8543C369.044 40.8543 373.145 48.0781 373.145 56.5244L373.145 82.0855L388.885 82.0855L388.885 54.6351C388.885 38.5204 379.242 25.5176 360.066 25.5176C340.89 25.5176 331.469 38.6316 331.469 54.6351L331.469 82.0855L347.208 82.0855L347.208 56.5244C347.208 48.0781 351.199 40.8543 360.066 40.8543Z"
      fill="#7B61FF"
    />
    <Path d="M398.204 101.892L322 101.892L331.525 87.9999L388.678 87.9999L398.204 101.892Z" fill="#7B61FF" />
    <G transform="rotate(-180 360.255 101.387)" fill="#7B61FF">
      <Ellipse
        cx="360.255"
        cy="101.387"
        rx="10.3914"
        ry="10.4189"
        transform="rotate(-180 360.255 101.387)"
        fill="#7B61FF"
      />
    </G>
  </Svg>
);
